import {
  makeStyles,
  Typography,
  Button,
  ConfirmationDialog,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  useCrmDialogs,
  useUser,
  useUserPermissions,
  useToggle
} from '@paintscout/ui'
import type { Contact } from '@paintscout/api'
import { useContactQuery } from '@paintscout/api'
import type { DialogProps } from '@paintscout/ui'
import { useContactMutations } from 'shared/hooks'
import { useSnackbar } from 'notistack'
import React, { useCallback, useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import ContactForm from 'src/components/forms/ContactForm'
import ClearIcon from '@material-ui/icons/Clear'
import { useHistory } from 'react-router-dom'
import { isUserAssigned } from '@paintscout/util/builder'
import { track } from 'shared/util/analytics'
import { useConfirmBeforeCancel } from '../useConfirmBeforeCancel'

const useStyles = makeStyles((theme) => ({
  confirm: {
    padding: theme.spacing(1.5, 3)
  },
  leftButtonDiv: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  swap: { color: theme.paintscout.palette.brand.Neutral.P60 },
  delete: {
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.error.main
    },
    '&:active': {
      color: theme.palette.error.main
    }
  },
  overflowVisible: {
    overflow: 'visible'
  }
}))

export interface ContactDialogProps extends DialogProps {
  contactId?: string
  defaultCompany?: {
    id: string
    name: string
  }
  queryString?: string
  disableDelete?: boolean
  onConfirm: (contact: Contact) => any
  confirmBeforeCancelWhenDirty?: boolean
  onSwap?: () => any
  onCancel?: (event: React.MouseEvent<HTMLElement>) => any
  onBack?: (event: React.MouseEvent<HTMLElement>) => any
  hideCompany?: boolean
}

function ContactDialog({
  contactId,
  defaultCompany = { id: '', name: '' },
  queryString = '',
  onCancel,
  onConfirm,
  onSwap,
  onBack,
  disableDelete = false,
  hideCompany: _hideCompany = false,
  confirmBeforeCancelWhenDirty = false,
  ...baseDialogProps
}: ContactDialogProps) {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const { user } = useUser()
  const { openDialog, dismissAllDialogs, dismissDialog, crmContext = {} } = useCrmDialogs()
  const { subtitle, company } = crmContext
  const defaultCompanyName = company?.name || defaultCompany.name
  const defaultCompanyId = company?.id || defaultCompany.id
  const hideCompany = _hideCompany || company?.hide

  const { permissionToEditAllContacts } = useUserPermissions()
  const { value: isSaving, setValue: setSaving } = useToggle(false)
  const { deleteContacts, saveContact } = useContactMutations()
  const history = useHistory()

  const {
    data,
    loading: fetching,
    error
  } = useContactQuery({
    variables: {
      id: contactId
    },
    fetchPolicy: 'cache-and-network',
    skip: !contactId
  })
  const { searchFirst, searchLast } = queryString.split(' ').reduce(
    (acc, curr, i) => {
      switch (i) {
        case 0:
          return { ...acc, searchFirst: curr }
        case 1:
          return { ...acc, searchLast: curr }
        default:
          return acc
      }
    },
    { searchFirst: '', searchLast: '' }
  )

  const [contact, setContact] = useState(
    contactId
      ? data?.contact
      : {
          ...data?.contact,
          company: defaultCompanyName,
          companyDocId: defaultCompanyId,
          first_name: searchFirst,
          last_name: searchLast
        }
  )
  const loading = (fetching && !contact && !!contactId) || isSaving
  const isCreating = !contactId
  const [isFormDirty, setIsFormDirty] = useState(false)
  const deleteEnabled =
    !disableDelete && (permissionToEditAllContacts || isUserAssigned(contact, user.user_id)) && contact?._id
  const handleRequestCancel = useConfirmBeforeCancel({
    confirmBeforeCancelWhenDirty,
    isFormDirty,
    onCancel
  })

  const handleRequestBack = useCallback(
    (event?: React.MouseEvent<HTMLElement>) => {
      if (onBack) {
        onBack(event as React.MouseEvent<HTMLElement>)
      } else {
        onCancel?.(event as React.MouseEvent<HTMLElement>)
      }
    },
    [onBack, onCancel]
  )
  useEffect(() => {
    if (error) {
      Sentry.captureException(error, { tags: { contactId } })
      enqueueSnackbar('Unable to load Contact', { variant: 'error' })
      onCancel(null)
    }
  }, [error])

  useEffect(() => {
    if (data) {
      setContact(data.contact)
    }
  }, [data])

  const handleDelete = useCallback(
    async (_: React.FormEvent<HTMLElement>) => {
      openDialog(ConfirmationDialog, {
        message: (
          <span>
            This will permanently delete{' '}
            {`${contact?.first_name ?? ''}${contact?.last_name ? ' ' + contact.last_name : ''}`}.
          </span>
        ),
        onConfirm: async () => {
          try {
            track('contact_deleted', { contactId: contact._id })
            await deleteContacts([contact._id])
            enqueueSnackbar('Contact successfully deleted', { variant: 'success' })
            dismissAllDialogs()
            history.push('/people')
          } catch (error) {
            Sentry.captureException(error, { tags: { contactId: contact._id } })
            enqueueSnackbar('Unable to delete Contact', { variant: 'error' })
          }
        },
        onCancel: dismissDialog
      })
    },
    [contact]
  )

  return (
    <Dialog
      open={open}
      maxWidth="md"
      {...baseDialogProps}
      onClose={() => handleRequestCancel()}
      classes={{ paper: classes.overflowVisible }}
    >
      <DialogTitle
        subtitle={subtitle}
        loading={loading}
        rightContent={
          <IconButton size="small" onClick={handleRequestCancel} data-testid="contact-dialog-close-button">
            <ClearIcon />
          </IconButton>
        }
      >
        <Typography variant="h3">{isCreating ? 'Create New' : 'Edit'} Contact</Typography>
      </DialogTitle>
      <DialogContent>
        <ContactForm
          id="contact-dialog-form"
          data-testid="contact-dialog-form"
          hideCompany={hideCompany}
          contact={contact}
          onDirtyChange={setIsFormDirty}
          onSubmit={handleSubmit}
          disabled={loading}
          onBack={handleRequestBack}
        />
      </DialogContent>
      <DialogActions
        leftButton={
          <div className={classes.leftButtonDiv}>
            {onBack ? (
              <Button variant="textNoPadding" onClick={() => handleRequestBack()}>
                Back
              </Button>
            ) : null}
            {isCreating || !deleteEnabled ? null : (
              <Button className={classes.delete} onClick={handleDelete} variant={'text'} disabled={loading}>
                Delete
              </Button>
            )}
            {!onSwap ? null : (
              <Button className={classes.swap} onClick={onSwap} variant={'textNoPadding'} disabled={loading}>
                Swap Contact
              </Button>
            )}
          </div>
        }
      >
        <Button
          className={classes.confirm}
          type="submit"
          form="contact-dialog-form"
          variant={'contained'}
          disabled={loading}
          data-testid="contact-dialog-confirm-button"
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )

  async function handleSubmit(newContact: Contact) {
    track(isCreating ? 'contact_created' : 'contact_updated', {
      contactId: newContact._id,
      hasCompany: !!newContact.company,
      hasEmail: !!newContact.email,
      leadSource: newContact.lead_source
    })
    setSaving(true)
    if (!!contact && !contact.company && !!newContact.company) {
      // If and individual contact is swapped to a company contact, remove their loan applications
      newContact.wisetackLoanApplications = null
    }
    try {
      const saveResult = await saveContact({ contact: newContact })

      onConfirm(saveResult.contact)
    } catch (error) {
      Sentry.captureException(error, { tags: { contactId } })
      const message = error?.graphQLErrors?.[0]?.message || 'Unable to save Contact'
      enqueueSnackbar(message, { variant: 'error' })
    }
    setSaving(false)
  }
}

export default ContactDialog
